/**
 * Web implementation of the social sign-in seam (ELARA-728).
 *
 * On the browser the OAuth dance is a popup: `signInWithPopup` opens the provider's consent window and
 * resolves with a Firebase `UserCredential`. This preserves the pre-ELARA-728 web behaviour (Google
 * sign-in) while routing it through the shared {@link SocialAuthPort} so `SessionState` is identical on
 * web and React Native.
 *
 * Apple is offered on web too (`isAppleAvailable: true`): Firebase's popup handles the Apple OAuth
 * redirect, so no native module or nonce is needed — `OAuthProvider('apple.com')` through the same
 * `signInWithPopup` path as Google.
 */
import type { UserCredential } from 'firebase/auth';

import type { AuthContainer } from '@elara/client-core/containers/FirebaseAuthContainer';
import type { SocialAuthPort, SocialSignInOutcome } from '@elara/client-core/lib/social-auth-port';

/** Firebase auth error codes that mean the user dismissed/aborted the popup rather than a real failure. */
const POPUP_CANCELLATION_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

function isPopupCancellation(error: unknown): error is { code: string } {
  const code = (error as { code?: string } | undefined)?.code;
  return typeof code === 'string' && POPUP_CANCELLATION_CODES.has(code);
}

export function createWebSocialAuthPort(auth: AuthContainer): SocialAuthPort {
  return {
    isAppleAvailable: true,
    async signInWithGoogle(): Promise<SocialSignInOutcome> {
      const provider = new auth.GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      return runPopup(async () => auth.signInWithPopup(provider));
    },
    async signInWithApple(): Promise<SocialSignInOutcome> {
      const provider = new auth.OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      return runPopup(async () => auth.signInWithPopup(provider));
    },
  };
}

/** Runs a popup sign-in, mapping a user-dismissed popup to a `cancelled` outcome. */
async function runPopup(open: () => Promise<UserCredential>): Promise<SocialSignInOutcome> {
  try {
    const credential = await open();
    return { status: 'signed-in', credential };
  } catch (error) {
    if (isPopupCancellation(error)) return { status: 'cancelled' };
    throw error;
  }
}
